import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { CartSignalInput } from '@capsico/contracts';
import { errorMessage, errorStatus, errorRef } from '../api/client';
import { applyGrowthCode, sendTouch } from '../api/growth';
import { sendCartSignal } from '../api/notify';
import { startEventFlushing, track } from '../api/track';
import { onPushTap } from '../native/push';
import { appPathFor } from './deepLink';
import {
  captureEntry,
  clearRefCode,
  clearTouch,
  pendingEntry,
  setPendingOfferCode,
  withoutEntryParams,
} from './growthEntry';
import { registerThisDevice } from './pushDevice';
import { cartSummary, useCart } from './useCart';
import { useSession } from './useSession';
import { showToast } from './useToast';

const CART_SIGNAL_DEBOUNCE_MS = 2_000;

/** A 4xx means the server looked at it and said no (bad / own / used code): do not retry. */
const isFinal = (error: unknown) => {
  const status = errorStatus(error);
  return status !== null && status >= 400 && status < 500;
};

/**
 * App-wide W18 background work (no UI): capture `?ref=` / QR scan / campaign entries and send them
 * once signed in, register this phone for push and open a tapped notification's screen, and
 * report the cart for the cart-left-behind reminder, and (W19) record app_open + flush buffered
 * app events. Every call is fail-soft.
 */
export function useAppRuntime(messages: {
  codeApplied: (code: string) => string;
  offerReady: (code: string) => string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const session = useSession();
  const signedIn = session.signedIn;
  const identityId = session.me?.identityId ?? null;
  const cart = useCart();
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  /* 1. Entry capture: store ?ref / ?scan / ?campaign / ?offer, then tidy the address bar. */
  useEffect(() => {
    if (!captureEntry(location.search)) return;
    navigate(`${location.pathname}${withoutEntryParams(location.search)}${location.hash}`, {
      replace: true,
    });
  }, [location.pathname, location.search, location.hash, navigate]);

  /* 2. Once signed in: send the attribution touch and apply a pending friend / creator code. */
  const sentFor = useRef<string | null>(null);
  useEffect(() => {
    if (!signedIn || !identityId) {
      sentFor.current = null;
      return;
    }
    const entry = pendingEntry();
    const key = `${identityId}:${entry.scanToken ?? ''}:${entry.campaign ?? ''}:${entry.refCode ?? ''}`;
    if (sentFor.current === key) return;
    sentFor.current = key;
    void (async () => {
      if (entry.scanToken || entry.campaign) {
        try {
          const result = await sendTouch(
            entry.scanToken ? { scanToken: entry.scanToken } : { campaign: entry.campaign ?? '' },
          );
          clearTouch();
          if (result.offerCode) {
            setPendingOfferCode(result.offerCode);
            showToast(messagesRef.current.offerReady(result.offerCode), { tone: 'positive' });
          }
        } catch (error) {
          if (isFinal(error)) clearTouch();
        }
      }
      if (entry.refCode) {
        try {
          const result = await applyGrowthCode(entry.refCode);
          clearRefCode();
          if (result.offer) setPendingOfferCode(result.offer.code);
          showToast(result.message || messagesRef.current.codeApplied(entry.refCode), {
            tone: 'positive',
          });
          void client.invalidateQueries({ queryKey: ['referral'] });
        } catch (error) {
          if (isFinal(error)) {
            clearRefCode();
            showToast(errorMessage(error), { tone: 'negative', ref: errorRef(error) });
          }
        }
      }
    })();
  }, [signedIn, identityId, client, location.key]);

  /* 3. Push: register after sign-in (once per signed-in person on this launch) + open taps. */
  const registeredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!signedIn || !identityId) {
      registeredFor.current = null;
      return;
    }
    if (registeredFor.current === identityId) return;
    registeredFor.current = identityId;
    void registerThisDevice();
  }, [signedIn, identityId]);

  useEffect(() => onPushTap((link) => navigate(appPathFor(link))), [navigate]);

  /* 4. Cart signal (debounced). An emptied cart clears the reminder with itemCount 0. */
  const lastSent = useRef<string | null>(null);
  const lastOutlet = useRef<string | null>(null);
  const summary = cartSummary(cart);
  const signal: CartSignalInput | null = cart
    ? { outletId: cart.outletId, itemCount: summary.count, totalMinor: summary.estimateMinor }
    : lastOutlet.current
      ? { outletId: lastOutlet.current, itemCount: 0, totalMinor: 0 }
      : null;
  const signalKey = signal ? JSON.stringify(signal) : null;
  useEffect(() => {
    if (cart) lastOutlet.current = cart.outletId;
  }, [cart]);
  useEffect(() => {
    if (!signedIn || !signal || !signalKey || signalKey === lastSent.current) return;
    const timer = window.setTimeout(() => {
      lastSent.current = signalKey;
      void sendCartSignal(signal).catch(() => {
        // Reminders are best-effort; try again on the next cart change.
        lastSent.current = null;
      });
      if (signal.itemCount === 0) lastOutlet.current = null;
    }, CART_SIGNAL_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // `signal` is derived from `signalKey`; keying on the string avoids resending on every render.
  }, [signedIn, signalKey]);

  /* 5. W19 events: one app_open per launch, then flush on focus / visible / back online. */
  useEffect(() => {
    if (!openTracked) {
      openTracked = true;
      track('app_open');
    }
    return startEventFlushing();
  }, []);
}

/** Module-level so React StrictMode's double effect run still records one app_open per launch. */
let openTracked = false;
